/**
 * Pure assembly of an NTP-style clock sampling burst (see player-core
 * clock-offset.ts). The hook supplies the transport (`GET /api/device/time`)
 * and the wall clock; this module owns the sequencing so it is testable
 * without network or DOM.
 */

import type { ClockSample } from "@workspace/player-core";

/**
 * Run `count` sequential round-trips against the server clock. Failed
 * round-trips are skipped — estimateClockOffset works from whatever usable
 * samples remain (and yields null when none survive).
 */
export async function collectClockSamples(
  count: number,
  fetchServerTimeMs: () => Promise<number>,
  nowMs: () => number = () => Date.now(),
): Promise<ClockSample[]> {
  const samples: ClockSample[] = [];
  for (let i = 0; i < count; i += 1) {
    const sentAtMs = nowMs();
    try {
      const serverTimeMs = await fetchServerTimeMs();
      samples.push({ sentAtMs, serverTimeMs, receivedAtMs: nowMs() });
    } catch {
      // A lost round-trip only shrinks the burst; the min-RTT pick is robust.
    }
  }
  return samples;
}
